import { FilePageDoc, SpecificationPageDoc } from 'common';
import { SpecificationDivision, SpecificationSearchResult, SpecificationSection } from '..';
import { authFetch, getAccessToken } from './AuthService';

export class SpecificationService {
  static MasterFormatDivisions = {
    'PROCUREMENT AND CONTRACTING REQUIREMENTS GROUP': {
      '00': 'Procurement and Contracting Requirements',
    },
    'General Requirements Subgroup': {
      '01': 'General Requirements',
    },
    'Facility Construction Subgroup': {
      '02': 'Existing Conditions',
      '03': 'Concrete',
      '04': 'Masonry',
      '05': 'Metals',
      '06': 'Wood, Plastics, and Composites',
      '07': 'Thermal and Moisture Protection',
      '08': 'Openings',
      '09': 'Finishes',
      '10': 'Specialties',
      '11': 'Equipment',
      '12': 'Furnishings',
      '13': 'Special Construction',
      '14': 'Conveying Equipment',
    },
    'Former Divisions (Now Merged or Reorganized)': {
      '15': 'Mechanical (Merged into Divisions 21-23)',
      '16': 'Electrical (Merged into 26)',
      '17': 'Special Construction (Merged into Divisions 13 & 14)',
      '18': 'Reserved for Future Use',
      '19': 'Reserved for Future Use',
      '20': 'Reserved for Future Use',
    },
    'Facility Services Subgroup': {
      '21': 'Fire Suppression',
      '22': 'Plumbing',
      '23': 'Heating, Ventilating, and Air Conditioning (HVAC)',
      '25': 'Integrated Automation',
      '26': 'Electrical',
      '27': 'Communications',
      '28': 'Electronic Safety and Security',
      '29': 'Reserved for Future Use',
      '30': 'Reserved for Future Use',
    },
    'Site and Infrastructure Subgroup': {
      '31': 'Earthwork',
      '32': 'Exterior Improvements',
      '33': 'Utilities',
      '34': 'Transportation',
      '35': 'Waterway and Marine Construction',
      '36': 'Reserved for Future Use',
      '37': 'Reserved for Future Use',
      '38': 'Reserved for Future Use',
      '39': 'Reserved for Future Use',
    },
    'Process Equipment Subgroup': {
      '40': 'Process Interconnections',
      '41': 'Material Processing and Handling Equipment',
      '42': 'Process Heating, Cooling, and Drying Equipment',
      '43': 'Process Gas and Liquid Handling, Purification and Storage Equipment',
      '44': 'Pollution and Waste Control Equipment',
      '45': 'Industry-Specific Manufacturing Equipment',
      '46': 'Water and Wastewater Equipment',
      '48': 'Electrical Power Generation',
    },
  };

  static MasterFormatSections = (() => {
    const flat: { [num: string]: string } = {};
    Object.values(SpecificationService.MasterFormatDivisions).forEach(group => {
      Object.entries(group).forEach(([num, title]) => {
        flat[num] = title;
      });
    });
    return flat;
  })();

  static scanPages(pages: SpecificationPageDoc[], numbersToIgnore: string[]): SpecificationPageDoc[] {
    return pages
      .sort((p1, p2) => Number(p1._id) - Number(p2._id))
      .map((page: SpecificationPageDoc, index) => {
        if (!page.body || page.body.length < 2) {
          return page;
        }
        if (page.ignore) {
          return page;
        }
        const section_num = page.section_num || this.getLastSectionNum(page.body, numbersToIgnore);
        if (!section_num) {
          return page;
        }
        const title = page.title || this.getTitle(page.body.slice(0, 200), section_num);
        return { ...page, section_num, title };
      });
  }

  static getLastSectionNum(text: string, numbersToIgnore: string[]): string {
    const sectionNumbers = this.findSectionNumbers(text, numbersToIgnore);
    return sectionNumbers ? sectionNumbers[sectionNumbers.length - 1] : null;
  }

  static findSectionNumbers(text: string, numbersToIgnore: string[]): string[] {
    numbersToIgnore.forEach(num => (text = text.replace(new RegExp(`/${num}/g`), '')));
    const matches = text.match(/\s[0-9]{2} ?[0-9]{2} ?[0-9]{2}/g);
    return matches ? matches.map(m => m.trim().replace(/[ \.]/g, '')) : null;
  }

  static getTitle(text, sectionNum) {
    const sectionNumSpaces = sectionNum.split('').join(' ?');
    return this.findTitle(text, sectionNum) || this.findTitle(text, sectionNumSpaces);
  }

  static findTitle(text: string, pattern: string) {
    const regex = new RegExp(pattern);
    const match = regex.exec(text);
    if (!match) return null;
    const endIndex = match.index + match[0].length;
    const afterNum = text.substring(endIndex).trim();
    return this.findFirstValidTitle(afterNum.split('\n'));
  }

  static findFirstValidTitle(parts) {
    for (var i = 0; i < parts.length; i++) {
      // Find first string that is more than 3 chars long and includes letters
      // Remove the first characters from the string if they are not A-Z,a-z. This removes dots, hyphens etc.
      if (parts[i].length > 3 && /[a-zA-Z]/.test(parts[i]))
        return parts[i]
          .trim()
          .replace(/^[^A-Za-z]+/, '')
          .trim();
    }
    return null;
  }

  static groupByDivision(pages: SpecificationPageDoc[]): SpecificationDivision[] {
    const sections: SpecificationSection[] = this.groupBySectionNum(pages);
    const titles = this.getDivisionTitles();
    const divisionsMap = new Map<string, SpecificationDivision>();
    let division: SpecificationDivision;
    sections.forEach(section => {
      const divisionNum = section.num.substring(0, 2);
      const title = titles[divisionNum];

      if (divisionNum) {
        if (!divisionsMap.has(divisionNum)) {
          divisionsMap.set(divisionNum, {
            num: divisionNum,
            title,
            sections: [section],
            collapsed: localStorage.getItem(`specification-division-${divisionNum}`) == 'collapsed',
          });
        } else {
          divisionsMap.get(divisionNum).title = divisionsMap.get(divisionNum).title || title;
          divisionsMap.get(divisionNum).sections.push(section);
        }
      } else {
        console.error(`No title found with num ${divisionNum}`);
      }
    });
    return Array.from(divisionsMap.values()).sort((a, b) => {
      return parseInt(a.num, 10) - parseInt(b.num, 10);
    });
  }

  static getDivisionTitles(): any {
    let subGroups = {};
    Object.keys(this.MasterFormatDivisions).forEach(majorTitle => {
      const subGroup = this.MasterFormatDivisions[majorTitle];
      subGroups = { ...subGroups, ...subGroup };
    });
    return subGroups;
  }

  static findDivisionTitle(divisionNum: string): string {
    Object.keys(this.MasterFormatDivisions).forEach(majorTitle => {
      const subGroup = this.MasterFormatDivisions[majorTitle];
      const divisionNums: string[] = Object.keys(subGroup);
      if (divisionNums.includes(divisionNum)) return subGroup[divisionNum];
    });
    return null;
  }

  static groupBySectionNum(pages: SpecificationPageDoc[]): SpecificationSection[] {
    const sections: SpecificationSection[] = [];
    let section: SpecificationSection;
    pages.forEach(page => {
      if (!page.section_num) return; // skip pages where nothing extracted

      if (!section) {
        section = { num: page.section_num, pages: [page] };
      } else if (page.section_num == section.num) {
        section.pages.push(page);
      } else {
        sections.push(section);
        section = { num: page.section_num, pages: [page] };
      }
    });
    sections.push(section);
    return sections.filter(s => !!s);
  }

  static getOccurrences(page: FilePageDoc, substring: string, snippetLength: number = 20): string[] {
    const regex = new RegExp(substring, 'gi'); // Case-insensitive global search
    const results: string[] = [];
    function findMatches(text: string) {
      let match;
      while ((match = regex.exec(text)) !== null) {
        const matchIndex = match.index;
        const matchText = match[0];

        // Find the nearest space before snippetLength
        let start = Math.max(0, matchIndex - snippetLength);
        while (start > 0 && text[start] !== ' ') start--; // Move left to nearest space

        // Find the nearest space after snippetLength
        let end = Math.min(text.length, matchIndex + matchText.length + snippetLength);
        while (end < text.length && text[end] !== ' ') end++; // Move right to nearest space

        // Extract before, match, and after
        const before = text.substring(start, matchIndex).trim();
        const after = text.substring(matchIndex + matchText.length, end).trim();
        results.push(`${before} **${matchText}** ${after}`);
      }
    }
    // Search in title and body
    if (page.title) findMatches(page.title);
    if (page.body) findMatches(page.body);

    return results;
  }

  static searchPages(pages: SpecificationPageDoc[], searchTerm: string): SpecificationSearchResult[] {
    return pages
      .map(page => {
        return {
          count: this.countMatches(page, searchTerm),
          page: page,
        };
      })
      .filter(result => result.count > 0);
  }

  static countMatches(page: FilePageDoc, substring: string): number {
    const regex = new RegExp(substring, 'gi');
    let count = 0;
    let match;
    while ((match = regex.exec(page.body)) !== null) {
      count++;
    }
    return count;
  }

  static async summariseText(text: string): Promise<{ summary: string; cleanedText: string }> {
    const response = await authFetch(`${globalThis.ccLambdaBaseUrl}/fn/submittals/summarise-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ text }),
    });
    if (!response.ok) {
      throw new Error('Failed to summarise text');
    }
    return response.json();
  }
}
